import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { DownloadToast } from '@/components/download/DownloadToast';

/**
 * Represents a downloadable file item
 */
export interface DownloadableFile {
  /** URL to download the file from */
  downloadUrl: string;
  /** Display name of the file */
  name: string;
  /** File extension (without the dot) */
  fileExtension?: string | null;
}

/**
 * Progress tracking for individual file downloads
 */
export interface DownloadProgressItem {
  fileName: string;
  status: 'pending' | 'fetching' | 'zipping' | 'error' | 'complete' | 'fallback';
  progress: number;
}

interface UseDownloadOptions {
  /** Callback when download completes successfully */
  onComplete?: () => void;
  /** Callback when download fails */
  onError?: (error: string) => void;
  /** Default zip filename for bulk downloads */
  zipFilename?: string;
  /** Show toast notifications (default: true) */
  showToast?: boolean;
}

/**
 * A reusable hook for downloading files - handles both single file downloads
 * and bulk downloads (zipped together). Shows a nice progress toast.
 *
 * @example
 * ```tsx
 * const { downloadFile, downloadFiles, isDownloading } = useDownload();
 *
 * // Single file download
 * downloadFile({ downloadUrl: blob.download_url, name: blob.name, fileExtension: blob.file_extension });
 *
 * // Bulk download (automatically zipped)
 * downloadFiles(selectedBlobs.map(b => ({ downloadUrl: b.download_url, name: b.name, fileExtension: b.file_extension })));
 * ```
 */
export const useDownload = (options: UseDownloadOptions = {}) => {
  const {
    onComplete,
    onError,
    zipFilename = 'documents.zip',
    showToast = true,
  } = options;

  const [downloadProgress, setDownloadProgress] = useState<
    DownloadProgressItem[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);

  // Track toast ID for updates
  const toastId = useRef<string | number | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearCompletedDownloads = useCallback(() => {
    setDownloadProgress((prev) => prev.filter((p) => p.status !== 'complete'));
  }, []);

  /**
   * Helper to build filename from file info
   */
  const buildFilename = (file: DownloadableFile): string => {
    return file.name + (file.fileExtension ? `.${file.fileExtension}` : '');
  };

  /**
   * Trigger a download via anchor tag (fallback for CORS issues)
   * This opens the file in a new tab/triggers browser download
   */
  const triggerAnchorDownload = useCallback((url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  /**
   * Show or update the download toast
   */
  const updateToast = useCallback(
    (
      progress: DownloadProgressItem[],
      generatingZip: boolean,
      currentError: string | null,
    ) => {
      if (!showToast) return;

      const handleDismiss = () => {
        if (toastId.current) {
          toast.dismiss(toastId.current);
          toastId.current = null;
        }
      };

      const renderToast = () => (
        <DownloadToast
          files={progress}
          isGeneratingZip={generatingZip}
          error={currentError}
          onDismiss={handleDismiss}
        />
      );

      if (toastId.current) {
        // Update existing toast
        toast.custom(renderToast, {
          id: toastId.current,
          duration: Infinity,
          position: 'bottom-right',
          unstyled: true,
        });
      } else {
        // Create new toast
        toastId.current = toast.custom(renderToast, {
          duration: Infinity,
          position: 'bottom-right',
          unstyled: true,
        });
      }
    },
    [showToast],
  );

  /**
   * Dismiss toast after delay
   */
  const dismissToastAfterDelay = useCallback((delay = 3000) => {
    setTimeout(() => {
      if (toastId.current) {
        toast.dismiss(toastId.current);
        toastId.current = null;
      }
    }, delay);
  }, []);

  /**
   * Download a single file
   */
  const downloadFile = useCallback(
    async (file: DownloadableFile) => {
      const filename = buildFilename(file);

      setIsDownloading(true);
      setError(null);

      const initialProgress: DownloadProgressItem[] = [
        { fileName: filename, status: 'fetching', progress: 50 },
      ];
      setDownloadProgress(initialProgress);
      updateToast(initialProgress, false, null);

      try {
        const response = await fetch(file.downloadUrl);
        if (!response.ok) {
          throw new Error(`Failed to download file: ${response.statusText}`);
        }

        const blob = await response.blob();
        saveAs(blob, filename);

        const completeProgress: DownloadProgressItem[] = [
          { fileName: filename, status: 'complete', progress: 100 },
        ];
        setDownloadProgress(completeProgress);
        updateToast(completeProgress, false, null);

        onComplete?.();
        dismissToastAfterDelay();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Download failed';
        setError(errorMessage);

        const errorProgress: DownloadProgressItem[] = [
          { fileName: filename, status: 'error', progress: 0 },
        ];
        setDownloadProgress(errorProgress);
        updateToast(errorProgress, false, errorMessage);

        onError?.(errorMessage);

        // Fall back to opening in new tab if fetch fails (e.g., CORS issues)
        window.open(file.downloadUrl, '_blank');

        dismissToastAfterDelay(5000);
      } finally {
        setIsDownloading(false);
      }
    },
    [onComplete, onError, updateToast, dismissToastAfterDelay],
  );

  /**
   * Download multiple files as a zip archive
   * Uses Promise.allSettled to handle CORS errors gracefully - files that fail
   * due to CORS are downloaded via anchor tag fallback instead of being zipped.
   */
  const downloadFiles = useCallback(
    async (files: DownloadableFile[], customZipFilename?: string) => {
      if (!files.length) return;

      // For single file, just download directly
      if (files.length === 1) {
        await downloadFile(files[0]);
        return;
      }

      setIsDownloading(true);
      setError(null);
      setIsGeneratingZip(false);

      // Initialize progress for each file
      const initialProgress: DownloadProgressItem[] = files.map((file) => ({
        fileName: buildFilename(file),
        status: 'pending' as const,
        progress: 0,
      }));
      setDownloadProgress(initialProgress);
      updateToast(initialProgress, false, null);

      // Track progress state that persists across async operations
      let currentProgress = [...initialProgress];

      // Helper to update progress for a specific file
      const updateFileProgress = (
        index: number,
        status: DownloadProgressItem['status'],
        progress: number,
      ) => {
        currentProgress = currentProgress.map((p, i) =>
          i === index ? { ...p, status, progress } : p,
        );
        setDownloadProgress([...currentProgress]);
        updateToast(currentProgress, false, null);
      };

      try {
        const zip = new JSZip();

        // Track results for each file
        interface FileResult {
          index: number;
          file: DownloadableFile;
          filename: string;
          blob?: Blob;
          usedFallback: boolean;
        }

        // Download each file - use Promise.allSettled to continue even if some fail
        const results = await Promise.allSettled(
          files.map(async (file, index): Promise<FileResult> => {
            const filename = buildFilename(file);

            // Update status to fetching
            updateFileProgress(index, 'fetching', 10);

            try {
              const response = await fetch(file.downloadUrl);
              if (!response.ok) {
                throw new Error(`Failed to fetch ${file.name}`);
              }

              const fileBlob = await response.blob();

              // Update status to zipping
              updateFileProgress(index, 'zipping', 50);

              return {
                index,
                file,
                filename,
                blob: fileBlob,
                usedFallback: false,
              };
            } catch {
              // Fetch failed (likely CORS) - use anchor fallback
              triggerAnchorDownload(file.downloadUrl, filename);

              // Mark as fallback (downloaded separately)
              updateFileProgress(index, 'fallback', 100);

              return {
                index,
                file,
                filename,
                usedFallback: true,
              };
            }
          }),
        );

        // Separate successful fetches from fallbacks
        const zippableFiles: FileResult[] = [];
        let fallbackCount = 0;
        let failedCount = 0;

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            if (result.value.usedFallback) {
              fallbackCount += 1;
            } else if (result.value.blob) {
              zippableFiles.push(result.value);
              // Mark as complete and add to zip
              updateFileProgress(result.value.index, 'complete', 100);
              zip.file(result.value.filename, result.value.blob);
            }
          } else {
            // Promise was rejected (shouldn't happen with our try/catch, but handle it)
            failedCount += 1;
          }
        });

        // Generate and save the zip file if we have any successful fetches
        if (zippableFiles.length > 0) {
          setIsGeneratingZip(true);
          updateToast(currentProgress, true, null);

          const content = await zip.generateAsync({ type: 'blob' });
          saveAs(content, customZipFilename || zipFilename);

          setIsGeneratingZip(false);
        }

        // Update toast with final state
        updateToast(currentProgress, false, null);

        // Determine completion status
        if (fallbackCount > 0 && zippableFiles.length > 0) {
          // Partial success: some in zip, some downloaded separately
          setError(null);
          onComplete?.();
        } else if (fallbackCount === files.length) {
          // All files used fallback - still successful, just not zipped
          setError(null);
          onComplete?.();
        } else if (zippableFiles.length === files.length) {
          // Full success: all files in zip
          setError(null);
          onComplete?.();
        } else if (failedCount > 0) {
          // Some files completely failed
          const errorMessage = `${failedCount} file(s) failed to download`;
          setError(errorMessage);
          onError?.(errorMessage);
        }

        dismissToastAfterDelay();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to download files';
        setError(errorMessage);
        updateToast(currentProgress, false, errorMessage);
        onError?.(errorMessage);
        dismissToastAfterDelay(5000);
      } finally {
        setIsDownloading(false);
        setIsGeneratingZip(false);
      }
    },
    [
      downloadFile,
      zipFilename,
      onComplete,
      onError,
      updateToast,
      dismissToastAfterDelay,
      triggerAnchorDownload,
    ],
  );

  return {
    /** Download a single file */
    downloadFile,
    /** Download multiple files as a zip */
    downloadFiles,
    /** Current download progress for all files */
    downloadProgress,
    /** Current error message, if any */
    error,
    /** Whether a download is in progress */
    isDownloading,
    /** Whether the zip file is being generated */
    isGeneratingZip,
    /** Clear the current error */
    clearError,
    /** Clear completed downloads from progress list */
    clearCompletedDownloads,
  };
};
